import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, X, Download, Copy, Search } from 'lucide-react';

export default function Particulars() {
  const [particulars, setParticulars] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [tableError, setTableError] = useState('');
  const [formError, setFormError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [role, setRole] = useState('');

  const [form, setForm] = useState({
    id: null,
    name: '',
    code: '',
  });

  const token = localStorage.getItem('token');

  const fetchParticulars = async (pageNum = 1, limitNum = 10, searchTerm = '') => {
    try {
      setLoading(true);
      let url = `particulars?paginate=true&page=${pageNum}&limit=${limitNum}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Handle the API response structure: { particulars: [], total: X, page: Y, limit: Z }
      const responseData = res.data;
      setParticulars(responseData.particulars || responseData.data || (Array.isArray(responseData) ? responseData : []));
      setTotal(responseData.total || 0);
      setPage(responseData.page || pageNum);
      setLimit(responseData.limit || limitNum);
      setTableError('');
    } catch (err) {
      console.error('Error fetching particulars:', err);
      setTableError('⚠️ Failed to fetch particulars. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDataRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setRole(user?.roles?.[0]?.name || '');
  }, []);

  useEffect(() => {
    if (fetchDataRef.current) {
      clearTimeout(fetchDataRef.current);
    }

    fetchDataRef.current = setTimeout(() => {
      fetchParticulars(page, limit, search);
    }, 300);

    return () => {
      if (fetchDataRef.current) {
        clearTimeout(fetchDataRef.current);
      }
    };
  }, [page, limit, search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdd = () => {
    setForm({ id: null, name: '', code: '' });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name,
      code: item.code,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) {
      setFormError('Name and Code are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        code: form.code,
      };

      if (form.id) {
        await axios.patch(`particulars/${form.id}`, payload);
        alert('✅ Particular updated successfully!');
      } else {
        await axios.post('particulars', payload);
        alert('✅ Particular added successfully!');
      }
      setIsModalOpen(false);
      fetchParticulars(page, limit, search);
    } catch (err) {
      console.error('Save error:', err);
      const errMsg =
        err.response?.data?.errors?.[0]?.error ||
        err.response?.data?.message ||
        'Failed to save particular';
      setFormError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`particulars/excel/download`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'particulars.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('❌ Failed to export particulars');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-6 md:p-10 text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
            📋 Particulars
          </h2>
          <p className="text-gray-500 mt-1">Manage all particulars here</p>
        </div>

        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          {role !== 'User' && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAdd}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-2.5 rounded-lg shadow-md flex-1 md:flex-none"
            >
              <Plus size={18} />
              <span>Add New</span>
            </motion.button>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 py-2.5 rounded-lg shadow-md flex-1 md:flex-none"
          >
            <Download size={18} />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={18} className="text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search by name or code..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
          value={search}
          onChange={handleSearch}
        />
      </div>

      {/* Table Error */}
      {tableError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-6 shadow-sm">
          {tableError}
        </div>
      )}

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Code</th>
                {role !== 'User' && (
                  <th className="py-4 px-6 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {particulars.length > 0 ? (
                particulars.map((item) => (
                  <tr key={item.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                    <td className="py-4 px-6 text-sm font-medium text-gray-800">{item.name}</td>
                    <td className="py-4 px-6 text-sm">
                      <code className="bg-gray-100 px-2 py-1 rounded text-blue-600 text-xs font-mono">{item.code}</code>
                    </td>
                    {role !== 'User' && (
                      <td className="py-4 px-6 text-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50 transition"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </motion.button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={role !== 'User' ? 3 : 2} className="py-10 text-center text-gray-500">
                    No particulars found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 transition font-medium"
          >
            Previous
          </button>
          <span className="text-gray-600 font-medium">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 transition font-medium"
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="flex justify-between items-center bg-gray-50 px-6 py-4 border-b">
                <h3 className="text-xl font-bold text-gray-800">
                  {form.id ? 'Edit Particular' : 'Add New Particular'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100 font-medium">
                    {formError}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Particular Name</label>
                  <input
                    name="name"
                    placeholder="Enter name"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Particular Code</label>
                  <input
                    name="code"
                    placeholder="Enter code"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.code}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-md transition disabled:opacity-70"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
